/**
 * Image resize integration (logoprocessing.paylync.com).
 *
 * Every image upload in the app is routed through `resizeImageFile()` BEFORE it
 * is sent to the backend: we POST the original File to the resize API, then
 * fetch the processed image back as a File so the existing upload flow stores
 * the resized version. The resize step is purely a "middle step" — the original
 * upload API that ran before is still the one that ultimately receives the file.
 *
 * Resize only — backgrounds are intentionally NOT removed (remove_background=false).
 *
 * On ANY failure (network error, non-200, malformed response, CORS, etc.) we
 * fall back to the original File and log a warning, so uploads never break.
 */

const RESIZE_API_BASE = 'https://logoprocessing.paylync.com';

// Rotate without a code change via VITE_RESIZE_API_KEY; falls back to the key
// already used elsewhere in the app (BioProfileEditor avatar processing).
const RESIZE_API_KEY =
    import.meta.env.VITE_RESIZE_API_KEY || 'xP9j-kL2m-vR5s-qT8n-73092';

// Resize to fit WITHIN 512x512 while PRESERVING the original aspect ratio
// (fit=contain → no padding, no crop). `fit=pad` previously forced a square
// and baked empty top/bottom (or side) space onto every non-square image,
// which then showed up in the widget. `contain` returns the image at its real
// aspect ratio (e.g. a 4:3 photo → 512x384), so widgets render it with no
// spacing. Background kept, PNG output. (Verified against the resize API:
// fit=contain returns the un-padded aspect-correct image; inside/scale-down
// are not supported by this API.)
const RESIZE_PARAMS =
    'width=512&height=512&fit=contain&remove_background=false&output_format=png&quality=90';

const RESIZE_URL = `${RESIZE_API_BASE}/api/v1/resize-image?${RESIZE_PARAMS}`;

/**
 * Only raster images are sent to the resizer. SVG (vector) and GIF (animated)
 * are passed through untouched — resizing them is meaningless or destructive.
 */
function isResizableImage(file) {
    return (
        !!file &&
        typeof file.type === 'string' &&
        file.type.startsWith('image/') &&
        file.type !== 'image/svg+xml' &&
        file.type !== 'image/gif'
    );
}

/**
 * Resize an image File via the resize API and return a NEW File holding the
 * processed image. Falls back to the original File on any error so the caller's
 * upload always proceeds.
 *
 * @param {File} file - the user-selected image file
 * @returns {Promise<File>} the resized File, or the original on failure / non-image
 */
export async function resizeImageFile(file) {
    if (!isResizableImage(file)) return file;

    try {
        const form = new FormData();
        form.append('file', file, file.name || 'image');

        const res = await fetch(RESIZE_URL, {
            method: 'POST',
            headers: { 'x-api-key': RESIZE_API_KEY },
            body: form,
        });
        const json = await res.json().catch(() => null);
        const processedUrl = json?.processed_images?.custom?.url;
        if (!res.ok || json?.success !== true || !processedUrl) {
            console.warn(
                '[resizeImage] resize API did not return a processed image; uploading original.',
                json?.message || `status ${res.status}`,
            );
            return file;
        }

        // Pull the processed bytes back so the existing upload flow can send them.
        const imgRes = await fetch(processedUrl);
        if (!imgRes.ok) {
            console.warn(
                '[resizeImage] could not fetch processed image; uploading original.',
                `status ${imgRes.status}`,
            );
            return file;
        }
        const blob = await imgRes.blob();
        const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
        return new File([blob], `${baseName}.png`, {
            type: blob.type || 'image/png',
            lastModified: Date.now(),
        });
    } catch (err) {
        console.warn('[resizeImage] resize failed; uploading original.', err);
        return file;
    }
}
